/**
 * Query Builder
 * TASK-293: Single responsibility - Build natural language search queries
 *
 * Constructs contextual queries for each data source based on client context
 */

import type { SearchContext, DateRangeInfo, SearchQueries } from './types';
import { getTopHoldings } from './contextBuilder';

/**
 * Build search queries for all data sources
 *
 * @param context - Search context with client and portfolio info
 * @param dateRange - Date range for the search
 * @returns SearchQueries object with queries for each source
 */
export function buildSearchQueries(
  context: SearchContext,
  dateRange: DateRangeInfo
): SearchQueries {
  const { symbols, companies } = getTopHoldings(context, 5);
  const dateFromFormatted = formatDateForQuery(dateRange.dateFrom);

  return {
    client: buildClientQuery(context),
    email: buildEmailQuery(context, dateFromFormatted),
    market: buildMarketQuery(symbols, companies),
    news: buildNewsQuery(context, companies),
    documents: buildDocumentsQuery(context, dateFromFormatted),
    // TODO: Backend search tools needed for calendar and portfolio alerts
    calendar: `Find upcoming meetings, appointments, and events for ${context.clientName}`,
    portfolio: `Get portfolio alerts, breaches, and rebalancing needs for ${context.clientName}`,
  };
}

/**
 * Build query for client information (database search)
 */
function buildClientQuery(context: SearchContext): string {
  return `Get profile, risk assessment, and investment preferences for client ${context.clientName}`;
}

/**
 * Build query for email search
 */
function buildEmailQuery(context: SearchContext, dateFrom: string): string {
  return `Find all emails with or about ${context.clientName} since ${dateFrom}`;
}

/**
 * Build query for market data
 */
function buildMarketQuery(symbols: string[], companies: string[]): string {
  if (symbols.length === 0 && companies.length === 0) {
    return 'Get general market overview and key indices performance';
  }

  const symbolsStr = symbols.length > 0 ? symbols.join(', ') : '';
  const companiesStr = companies.length > 0 ? companies.join(', ') : '';

  if (symbolsStr && companiesStr) {
    return `Get market data and performance for: ${symbolsStr} (${companiesStr})`;
  }

  return `Get market data and performance for: ${symbolsStr || companiesStr}`;
}

/**
 * Build query for news search
 */
function buildNewsQuery(context: SearchContext, companies: string[]): string {
  const targets = [context.clientName, ...companies.slice(0, 3)].join(', ');
  return `Find recent news about: ${targets}`;
}

/**
 * Build query for document search
 */
function buildDocumentsQuery(context: SearchContext, dateFrom: string): string {
  return `Find documents and reports for ${context.clientName} since ${dateFrom}`;
}

/**
 * Format date for natural language query
 */
function formatDateForQuery(isoDate: string): string {
  const date = new Date(isoDate);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

/**
 * Get query for a specific source type
 */
export function getQueryForSource(
  queries: SearchQueries,
  sourceType: keyof SearchQueries
): string {
  return queries[sourceType];
}

/**
 * Check if queries have meaningful content
 */
export function hasValidQueries(queries: SearchQueries): boolean {
  return Object.values(queries).every((q) => q.length > 0);
}
